import { useRef, useCallback, useEffect } from 'react';

/**
 * This hook help to easy add an infinite scroll behavior to the your
 * component. For that u need provide the `onLoadMore` function which will
 * fetch an additional data and `scrollRange` interval after which we need to
 * call `onLoadMore`.
 *
 * This hook return a ref object which we need to set to component which exist
 * our content.
 *
 * const scrollRef = useInfinityScroll(onLoadMore, 200);
 *
 * <TableComponent ref={scrollRef}>
 *   list_data.map(x => <p>x</p>)
 * </TableComponent>
 */
export const useInfinityScroll = <T extends HTMLElement = HTMLDivElement>(
  onLoadMore: () => void, scrollRange: number,
) => {
  const ref = useRef<T>(null);
  const lastScroll = useRef<number>(0);

  const handlerScroll = useCallback(
    () => {
      const component = ref.current;

      if (component) {
        const windowScrollElement = document.scrollingElement || document.documentElement;
        const windowScrollOffset = window.innerHeight + windowScrollElement.scrollTop;
        const componentBottomScrollOffset = component.offsetTop + component.clientHeight;
        const loadMore = windowScrollOffset > componentBottomScrollOffset - scrollRange;
        const isDownScrollDirection = windowScrollElement.scrollTop > lastScroll.current;

        if (isDownScrollDirection && loadMore) {
          onLoadMore();
        }

        lastScroll.current = windowScrollElement.scrollTop;
      }
    },
    [onLoadMore, scrollRange],
  );

  useEffect(
    () => {
      window.addEventListener('scroll', handlerScroll);

      return () => {
        window.removeEventListener('scroll', handlerScroll);
      };
    },
    [handlerScroll],
  );

  return ref;
};

/**
 * Custom hook for implementing infinite scroll behavior on a scrollable div,
 * triggering a callback to load more data when the user scrolls near the bottom.
 *
 * @param {Function} loadMore - Callback function to load more data when the user
 * scrolls near the bottom.
 * @param {number} [threshold=25] - The distance (in pixels) from the bottom of
 * the scrollable div at which the `loadMore` callback should be triggered.
 * @returns {Function} - A function to set the reference of the scrollable div
 * element.
 *
 * @example
 * const setScrollRef = useRelatedInfiniteScrollWithDogs(loadMoreData, 50);
 *
 * <div ref={setScrollRef} style={{ overflowY: 'scroll', height: '500px' }}>
 *   Scrollable content
 * </div>
 */
export const useRelatedInfiniteScroll = (loadMore: () => void, threshold = 25) => {
  const scrollableDivRef = useRef<HTMLElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollableDivRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollableDivRef.current;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    if (scrollBottom <= threshold) {
      loadMore();
    }
  }, [loadMore, threshold]);

  const setRef = useCallback((node: HTMLElement | null) => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.removeEventListener('scroll', handleScroll);
    }

    if (node) {
      node.addEventListener('scroll', handleScroll);
    }

    // @ts-ignore
    scrollableDivRef.current = node;
  }, [handleScroll]);

  return setRef;
};
